/* RadiusConfigRenderer.js */

"use strict";
const _ = require("lodash");
const uiCore = require("@gluenetworks/steve_ui_core");
const ButtonControl = uiCore.Control.Action.Button;
const InputTextControl = uiCore.Control.Textfield.InputText;
const InputNumberControl = uiCore.Control.Textfield.InputNumber;
const CheckboxControl = uiCore.Control.Boolean.InputCheckbox;
const ComboBoxControl = uiCore.Control.OptionSelection.Single.ComboBox;
const MultiCombobox = uiCore.Control.OptionSelection.Multiple.ComboBox;
const InputRadioControl = uiCore.Control.OptionSelection.Single.InputRadio;

class RadiusConfigRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("radius-config-gl");
        this._rootElem.component = this._model;
        this._radiusContainer = document.createElement("div");
        this._testButton = new ButtonControl({
            id: "test-radius",
            label: "Test RADIUS Connection",
            disabled: false,
            title: "Test the RADIUS configuration and connection",
        });
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on(
            "ActionGroupsFeedUpdated",
            (this._updateRoles = this._updateRoles.bind(this))
        );
        this._model.on(
            "enableRADIUS",
            (this._onRadiusEnableChange = this._onRadiusEnableChange.bind(this))
        );
        this._testButton.on(
            "UserClick",
            (this._onTestRadiusClicked = this._onTestRadiusClicked.bind(this))
        );
    }

    _unregisterListeners() {
        this._model.removeAllListeners("ActionGroupsFeedUpdated");
        this._model.removeListener("enableRADIUS", this._onRadiusEnableChange);
        this._testButton.removeListener("UserClick", this._onTestRadiusClicked);
        this._unregisterRADIUSListeners();
    }

    _registerRADIUSListeners() {
        this._radiusDefaultActionGroupId.on(
            "UserValue",
            this._gatherRadiusDataAndUpdateModel.bind(this)
        );
        this._mapActionGroupsCheckbox.on("UserValue", this._onMapActionGroupsChange.bind(this));
        this._radiusSomeOrgVisibility.on("UserValue", this._onSomeOrgVisChanged.bind(this));
        this._radiusOrgVisibility.on("UserValue", this._orgVisChanged.bind(this));
        this._enableAccountingCheckbox.on("UserValue", this._onEnableAccountingChanged.bind(this));
        Object.keys(this._radiusControls).forEach((radiusPropName) => {
            if (radiusPropName === "mapping") {
                Object.keys(this._radiusControls.mapping).forEach((mapProp) => {
                    if (mapProp === "actionGroupMapping") {
                        Object.keys(this._radiusControls.mapping.actionGroupMapping).forEach(
                            (authMapProp) => {
                                this._radiusControls.mapping.actionGroupMapping[authMapProp].on(
                                    "UserValue",
                                    this._gatherRadiusDataAndUpdateModel.bind(this)
                                );
                            }
                        );
                    } else {
                        this._radiusControls.mapping[mapProp].on(
                            "UserValue",
                            this._gatherRadiusDataAndUpdateModel.bind(this)
                        );
                    }
                });
            } else {
                this._radiusControls[radiusPropName].on(
                    "UserValue",
                    this._gatherRadiusDataAndUpdateModel.bind(this)
                );
            }
        });
    }

    _unregisterRADIUSListeners() {
        if (this._radiusContainer.firstChild) {
            this._radiusDefaultActionGroupId.removeListener(
                "UserValue",
                this._gatherRadiusDataAndUpdateModel
            );
            this._mapActionGroupsCheckbox.removeListener(
                "UserValue",
                this._onMapActionGroupsChange
            );
            this._radiusSomeOrgVisibility.removeListener("UserValue", this._onSomeOrgVisChanged);
            this._radiusOrgVisibility.removeListener("UserValue", this._orgVisChanged);
            this._enableAccountingCheckbox.removeListener(
                "UserValue",
                this._onEnableAccountingChanged
            );

            Object.keys(this._radiusControls).forEach((radiusPropName) => {
                if (radiusPropName === "mapping") {
                    Object.keys(this._radiusControls.mapping).forEach((mapProp) => {
                        if (mapProp === "actionGroupMapping") {
                            Object.keys(this._radiusControls.mapping.actionGroupMapping).forEach(
                                (authMapProp) => {
                                    this._radiusControls.mapping.actionGroupMapping[
                                        authMapProp
                                    ].removeListener(
                                        "UserValue",
                                        this._gatherRadiusDataAndUpdateModel
                                    );
                                }
                            );
                        } else {
                            this._radiusControls.mapping[mapProp].removeListener(
                                "UserValue",
                                this._gatherRadiusDataAndUpdateModel
                            );
                        }
                    });
                } else {
                    this._radiusControls[radiusPropName].removeListener(
                        "UserValue",
                        this._gatherRadiusDataAndUpdateModel
                    );
                }
            });
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        let promArr = [this._testButton.shutdown()];

        return Promise.all(promArr).then(
            () => {
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                if (this._radiusContainer.firstChild) {
                    _.forEach(this._radiusControls, (control, controlName) => {
                        if (controlName === "mapping") {
                            _.forEach(control, (mappingControl, mappingControlName) => {
                                if (mappingControlName === "actionGroupMapping") {
                                    _.forEach(mappingControl, (authControl) => {
                                        authControl.shutdown();
                                    });
                                } else {
                                    mappingControl.shutdown();
                                }
                            });
                        } else {
                            control.shutdown();
                        }
                    });
                    this._radiusControls = undefined;
                    this._radiusContainer.remove();
                    this._radiusContainer = undefined;
                    this._testButton.shutdown();
                    this._testButton = undefined;
                    this._radiusControls = undefined;
                    this._actionGroupMapElem.remove();
                    this._actionGroupMapElem = undefined;
                    this._radiusOrgVisibility.shutdown();
                    this._radiusOrgVisibility = undefined;
                    this._radiusSomeOrgVisibility.shutdown();
                    this._radiusSomeOrgVisibility = undefined;
                    this._mapActionGroupsCheckbox.shutdown();
                    this._mapActionGroupsCheckbox = undefined;
                    this._radiusDefaultActionGroupId.shutdown();
                    this._radiusDefaultActionGroupId = undefined;
                    this._enableAccountingCheckbox.shutdown();
                    this._enableAccountingCheckbox = undefined;
                }
            },
            (err) => {
                console.error(err);
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return this.model.whenModelIsReady().then(() => {
            this._onRadiusEnableChange(this.model._isEnabled, true);
            Promise.resolve();
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _getActionGroups() {
        if (this._model._isProvider && this.model.actionGroupsFeed) {
            return this.model.actionGroupsFeed.filter((role) => {
                if (role.systemInternal || role.seeded || (role.enabled && role.shared)) {
                    return true;
                }
                return false;
            });
        } else {
            return this.model.actionGroupsFeed.filter((role) => {
                if (!role.systemInternal) {
                    return true;
                }
                return false;
            });
        }
    }

    _renderRoles() {
        if (!this.model.actionGroupsFeed || !this._radiusControls) {
            return Promise.resolve();
        }
        const roles = this._getActionGroups();

        this._actionGroupMapElem.innerHTML = "";
        roles.forEach((actionGroupProp) => {
            //.getCacheAsArray()
            try {
                let actionGroupMapInputControl = new InputTextControl({darkTheme: true});
                this._radiusControls.mapping.actionGroupMapping[actionGroupProp.id] =
                    actionGroupMapInputControl;
                this._renderPair(
                    actionGroupProp.name,
                    actionGroupMapInputControl,
                    this._actionGroupMapElem,
                    true
                );
            } catch (err) {
                return Promise.reject(err);
            }
        });
        return Promise.resolve();
    }

    _renderPair(labelName, component, container, indent, title = "") {
        let labelElem = document.createElement("label");
        labelElem.innerText = labelName;
        labelElem.classList.add("form-label-gl");
        let cmpntElem = component.renderer.getRootElement();

        if (indent) {
            labelElem.classList.add("indented-gl");
            cmpntElem.classList.add("indented-gl");
        }

        if (title) {
            labelElem.title = title;
        }

        container.appendChild(labelElem);
        container.appendChild(cmpntElem);
    }

    _renderRadiusFields() {
        if (!this._radiusContainer.firstChild) {
            this._radiusControls = {};
            this._radiusControls.userDomain = new InputTextControl({darkTheme: true});
            this._renderPair(
                "RADIUS Domain*",
                this._radiusControls.userDomain,
                this._radiusContainer,
                false,
                "The domain name users include when signing in. Enter * if domain name not required"
            );
            this._radiusControls.stripDomain = new CheckboxControl({
                darkTheme: true,
                labelShown: true,
                label: "Send username to RADIUS server without domain",
                title: "Strips domain name off the username",
            });
            this._radiusControls.stripDomain.renderer.rootElement.classList.add("form-label-gl");
            this._radiusContainer.appendChild(
                this._radiusControls.stripDomain.renderer.rootElement
            );
            this._radiusControls.radiusUsersOnly = new CheckboxControl({
                darkTheme: true,
                labelShown: true,
                label: "Disable creation of local users",
                title: "Limits new users to only those in RADIUS",
            });
            this._radiusControls.radiusUsersOnly.renderer.rootElement.classList.add(
                "form-label-gl"
            );
            this._radiusContainer.appendChild(
                this._radiusControls.radiusUsersOnly.renderer.rootElement
            );

            this._radiusControls.host = new InputTextControl({darkTheme: true});
            this._renderPair(
                "Primary Host*",
                this._radiusControls.host,
                this._radiusContainer,
                false,
                "Host name or IP address of the RADIUS server"
            );
            this._radiusControls.port = new InputNumberControl({darkTheme: true, value: 8012});
            this._renderPair(
                "Primary Port*",
                this._radiusControls.port,
                this._radiusContainer,
                false,
                "Port of the RADIUS server"
            );

            this._radiusControls.secondaryHost = new InputTextControl({darkTheme: true});
            this._renderPair(
                "Secondary Host",
                this._radiusControls.secondaryHost,
                this._radiusContainer,
                false,
                "Host name or IP address of the secondary RADIUS server"
            );
            this._radiusControls.secondaryPort = new InputNumberControl({darkTheme: true});
            this._renderPair(
                "Secondary Port",
                this._radiusControls.secondaryPort,
                this._radiusContainer,
                false,
                "Port of the secondary RADIUS server"
            );

            this._radiusControls.requestTimeout = new InputNumberControl({
                darkTheme: true,
                value: 2000,
            });
            this._renderPair(
                "Request Timeout (Milliseconds)*",
                this._radiusControls.requestTimeout,
                this._radiusContainer,
                false,
                "Time allowed for the request to the RADIUS server to respond"
            );
            this._radiusControls.requestRetries = new InputNumberControl({
                darkTheme: true,
                value: 3,
            });
            this._renderPair(
                "Request Retries*",
                this._radiusControls.requestRetries,
                this._radiusContainer,
                false,
                "Number of times a connection to the RADIUS server will be attempted"
            );

            this._radiusControls.secret = new ComboBoxControl({darkTheme: true});
            this._radiusControls.secret.setOptions(this._model._credentialsList);

            this._renderPair(
                "RADIUS Server Secret Credential*",
                this._radiusControls.secret,
                this._radiusContainer,
                false,
                "Shared secret of the RADIUS server for the Gluware RADIUS client"
            );

            this._radiusContainer.appendChild(this._testButton.renderer.rootElement);
            this._radiusControls.mapping = {};
            let placeholder = "Enter an attribute name or a vendor ID/attribute ID";
            this._radiusControls.mapping.email = new InputTextControl({
                darkTheme: true,
                placeholder,
            });
            this._renderPair(
                "Custom Email Attribute",
                this._radiusControls.mapping.email,
                this._radiusContainer,
                false,
                "Attribute that contains user's email address"
            );
            this._radiusControls.mapping.first = new InputTextControl({
                darkTheme: true,
                placeholder,
            });
            this._renderPair(
                "Custom First Name Attribute",
                this._radiusControls.mapping.first,
                this._radiusContainer,
                false,
                "Attribute that contains user’s first name"
            );
            this._radiusControls.mapping.last = new InputTextControl({
                darkTheme: true,
                placeholder,
            });
            this._renderPair(
                "Custom Last Name Attribute",
                this._radiusControls.mapping.last,
                this._radiusContainer,
                false,
                "Attribute that contains user’s last name"
            );

            this._radiusControls.mapping.actionGroup = new InputTextControl({
                darkTheme: true,
                placeholder,
            });
            this._renderPair(
                "Role Attribute",
                this._radiusControls.mapping.actionGroup,
                this._radiusContainer,
                false,
                "Attribute that contains user's Gluware role"
            );

            this._mapActionGroupsCheckbox = new CheckboxControl({
                darkTheme: true,
                labelShown: true,
                label: "Map Role Attribute Value",
                title: "Create up to five RADIUS security groups mapped to Gluware roles",
            });
            this._radiusContainer.appendChild(this._mapActionGroupsCheckbox.renderer.rootElement);
            this._mapActionGroupsCheckbox.renderer.rootElement.classList.add("form-label-gl");

            this._radiusControls.mapping.actionGroupMapping = {};
            this._actionGroupMapElem = document.createElement("action-auth-map-elem");
            this._renderRoles();
            this._radiusContainer.appendChild(this._actionGroupMapElem);
            this._actionGroupMapElem.classList.add("hidden");

            this._radiusDefaultActionGroupId = new ComboBoxControl({darkTheme: true});
            this._renderPair(
                "Default Role",
                this._radiusDefaultActionGroupId,
                this._radiusContainer,
                false,
                "Default role given if no role attribute defined"
            );

            this._radiusOrgVisibility = new InputRadioControl({
                darkTheme: true,
                horizontal: true,
                options: [
                    {label: "All", value: "All"},
                    {label: "Some", value: "Some"},
                    {label: "Home Organization", value: "Home"},
                ],
                value: "Home",
            });
            this._renderPair(
                "Default Organization Visibility",
                this._radiusOrgVisibility,
                this._radiusContainer,
                false,
                "Attribute that contains a string of organization names the user can access"
            );

            this._radiusSomeOrgVisibility = new MultiCombobox({darkTheme: true});
            this._radiusContainer.appendChild(this._radiusSomeOrgVisibility.renderer.rootElement);

            this._enableAccountingCheckbox = new CheckboxControl({
                darkTheme: true,
                labelShown: true,
                label: "Enable Accounting",
                title: "Enables record keeping of sign in/sign off activity",
            });
            this._radiusContainer.appendChild(this._enableAccountingCheckbox.renderer.rootElement);
            this._enableAccountingCheckbox.renderer.rootElement.classList.add("form-label-gl");

            this._accountingElem = document.createElement("accounting-elem");
            this._radiusControls.accountingHost = new InputTextControl({darkTheme: true});
            this._renderPair(
                "Primary Accounting Host*",
                this._radiusControls.accountingHost,
                this._accountingElem
            );
            this._radiusControls.accountingPort = new InputNumberControl({
                darkTheme: true,
                value: 8013,
            });
            this._renderPair(
                "Primary Accounting Port*",
                this._radiusControls.accountingPort,
                this._accountingElem
            );
            this._radiusControls.secondaryAccountingHost = new InputTextControl({
                darkTheme: true,
            });
            this._renderPair(
                "Secondary Accounting Host",
                this._radiusControls.secondaryAccountingHost,
                this._accountingElem
            );
            this._radiusControls.secondaryAccountingPort = new InputNumberControl({
                darkTheme: true,
                value: 8013,
            });
            this._renderPair(
                "Secondary Accounting Port",
                this._radiusControls.secondaryAccountingPort,
                this._accountingElem
            );
            this._radiusContainer.appendChild(this._accountingElem);
            this._accountingElem.classList.add("hidden");

            this.rootElement.appendChild(this._radiusContainer);
            this._registerRADIUSListeners();

            this.setRadiusDataFromModel();
        }
    }

    _getRadiusDataFromFields() {
        let RADIUSConfig = {};
        Object.keys(this._radiusControls).forEach((radiusPropName) => {
            if (radiusPropName === "mapping") {
                Object.keys(this._radiusControls.mapping).forEach((mapProp) => {
                    if (mapProp === "actionGroupMapping") {
                        if (this._mapActionGroupsCheckbox.value) {
                            _.forEach(
                                this._radiusControls.mapping.actionGroupMapping,
                                (authControl, authProp) => {
                                    if (authControl.length > 0) {
                                        _.set(
                                            RADIUSConfig,
                                            `mapping.actionGroupMapping.${authProp}`,
                                            authControl
                                        );
                                    }
                                }
                            );
                        }
                    } else {
                        let controlValue = this._radiusControls.mapping[mapProp];
                        let mapPropName = "mapping." + mapProp;
                        if (controlValue.length > 0) {
                            _.set(RADIUSConfig, mapPropName, controlValue);
                        }
                    }
                });
            } else {
                let controlValue = this._radiusControls[radiusPropName].value;
                if (!_.isUndefined(controlValue)) {
                    if (!_.isString(controlValue)) {
                        RADIUSConfig[radiusPropName] = this._radiusControls[radiusPropName].value;
                    } else {
                        if (controlValue.length > 0) {
                            RADIUSConfig[radiusPropName] =
                                this._radiusControls[radiusPropName].value;
                        }
                    }
                }
            }
        });

        _.assign(RADIUSConfig, this._getDefaultOrgVisibility());
        _.assign(RADIUSConfig, this._getActionGroupProp());

        let mappings = this._getMappingProps();
        if (mappings) {
            RADIUSConfig.mapping = mappings;
        }

        return RADIUSConfig;
    }

    // TODO - Fix dirty check before applying commented code
    _gatherRadiusDataAndUpdateModel(/*...args*/) {
        let RADIUSConfig = this._getRadiusDataFromFields();
        // if(args[4]) {
        //     RADIUSConfig.accountingRemoved = true;
        // } else {
        //     _.unset(RADIUSConfig, 'accountingRemoved');
        // }
        if (!RADIUSConfig.accountingHost) {
            _.unset(RADIUSConfig, "accountingHost");
            _.unset(RADIUSConfig, "accountingPort");
        }
        if (!RADIUSConfig.secondaryAccountingHost) {
            _.unset(RADIUSConfig, "secondaryAccountingHost");
            _.unset(RADIUSConfig, "secondaryAccountingPort");
        }
        //number inputs becomes null instead of undefined or unset
        //other number inputs would have similar problem, but all are required
        if (RADIUSConfig.secondaryPort === null) {
            _.unset(RADIUSConfig, "secondaryPort");
        }
        this.model.setSaveModel(RADIUSConfig);
    }

    _getMappingProps() {
        let mappings = {};
        Object.keys(this._radiusControls.mapping).forEach((radiusPropName) => {
            if (radiusPropName === "actionGroupMapping") {
                if (this._mapActionGroupsCheckbox.value) {
                    Object.keys(this._radiusControls.mapping.actionGroupMapping).forEach(
                        (actionGroupPropName) => {
                            let value =
                                this._radiusControls.mapping.actionGroupMapping[actionGroupPropName]
                                    .value;
                            if (_.isString(value) && value.length > 0) {
                                _.set(mappings, `actionGroupMapping.${actionGroupPropName}`, value);
                            }
                        }
                    );
                }
            } else {
                let value = this._radiusControls.mapping[radiusPropName].value;
                if (_.isString(value) && value.length > 0) {
                    mappings[radiusPropName] = value;
                }
            }
        });
        if (!_.isEmpty(mappings)) {
            return mappings;
        } else {
            return undefined;
        }
    }

    _getDefaultOrgVisibility() {
        let returnVal = {};
        if (this._radiusOrgVisibility.value === "Some") {
            returnVal = {};
            this._radiusSomeOrgVisibility.value.forEach((orgId) => {
                returnVal[orgId] = {};
            });
        } else {
            returnVal = this._radiusOrgVisibility.value;
        }
        if (_.isEmpty(returnVal)) {
            return undefined;
        }
        return {defaultOrgVisibility: returnVal};
    }

    _buildActionGroupList() {
        let actionGroupOptions = [];
        const actionGroups = this._getActionGroups();
        if (!_.isEmpty(actionGroups)) {
            Object.keys(actionGroups).forEach((groupKey) => {
                if (!actionGroups[groupKey].getSystemInternal()) {
                    const name = actionGroups[groupKey].name;
                    const agUUID = actionGroups[groupKey].id;
                    actionGroupOptions.push({label: name, value: agUUID});
                }
            });
        }
        return actionGroupOptions;
    }

    _getActionGroupProp() {
        if (this._radiusDefaultActionGroupId.getValue()) {
            return {defaultActionGroupId: this._radiusDefaultActionGroupId.getValue()};
        }
        return {};
    }

    _setDefaultOrgVisValue(allOrHomeorSome) {
        if (allOrHomeorSome === "Some") {
            let orgApiList = [];
            this._radiusSomeOrgVisibility.visible = true;
            Object.keys(this.model.userInfo.authorizedOrganizations).forEach((orgId) => {
                let orgName = this.model.userInfo.authorizedOrganizations[orgId].name;
                orgApiList.push({label: orgName, value: orgId});
            });
            this._radiusSomeOrgVisibility.setOptions(orgApiList);
            let selectedOrgsObj = _.get(
                this.model.selectedOrgInfo,
                "radius.defaultOrgVisibility",
                false
            );
            if (_.isPlainObject(selectedOrgsObj)) {
                this._radiusSomeOrgVisibility.value = Object.keys(selectedOrgsObj);
            }
        } else {
            this._radiusSomeOrgVisibility.visible = false;
            this._radiusSomeOrgVisibility.setOptions([]);
        }
    }

    _setDefaultOrgVisValueFromModelValue() {
        let modelValue = _.get(this.model.selectedOrgInfo, "radius.defaultOrgVisibility", "All");
        let allOrHomeorSome;
        if (_.isString(modelValue)) {
            allOrHomeorSome = modelValue;
        } else if (_.isPlainObject(modelValue)) {
            allOrHomeorSome = "Some";
        }
        this._radiusOrgVisibility.value = allOrHomeorSome;
        this._setDefaultOrgVisValue(allOrHomeorSome);
    }

    setRadiusDataFromModel() {
        //Re-applied on every render so the list tracks the selected org. Must run before the
        //_setControlValue loop below, which assigns the selected credential.
        this._radiusControls.secret.setOptions(this._model._credentialsList || []);
        this._radiusDefaultActionGroupId.setOptions(this._buildActionGroupList());
        this._radiusDefaultActionGroupId.setValue(
            _.get(this.model.selectedOrgInfo, "radius.defaultActionGroupId")
        );
        //Organization Visibility
        this._setDefaultOrgVisValueFromModelValue();
        let haveActionGroupMappings = _.get(
            this.model.selectedOrgInfo,
            "radius.mapping.actionGroupMapping"
        );
        this._mapActionGroupsCheckbox.setValue(haveActionGroupMappings);
        this._showHideActionGroupMap();
        let accountingEnabled = !!_.get(this.model.selectedOrgInfo, "radius.accountingHost");
        this._enableAccountingCheckbox.setValue(accountingEnabled);
        this._showHideAccountingFields();
        Object.keys(this._radiusControls).forEach((radiusPropName) => {
            if (radiusPropName === "mapping") {
                Object.keys(this._radiusControls.mapping).forEach((mapProp) => {
                    if (mapProp === "actionGroupMapping") {
                        if (haveActionGroupMappings) {
                            Object.keys(this._radiusControls.mapping.actionGroupMapping).forEach(
                                (authMapProp) => {
                                    this._setControlValue(
                                        "mapping.actionGroupMapping." + authMapProp
                                    );
                                }
                            );
                        }
                    } else {
                        this._setControlValue("mapping." + mapProp);
                    }
                });
            } else {
                this._setControlValue(radiusPropName);
            }
        });
    }

    _setControlValue(radiusPropName) {
        if (!_.isUndefined(_.get(this.model.selectedOrgInfo, "radius." + radiusPropName))) {
            _.set(
                this._radiusControls,
                radiusPropName + ".value",
                _.get(this.model.selectedOrgInfo, "radius." + radiusPropName)
            );
        } else {
            _.set(this._radiusControls, radiusPropName + ".value", "");
        }
    }

    _hideRadiusFields() {
        this._radiusContainer.style.display = "none";
    }

    _showRadiusFields() {
        this._radiusContainer.style.display = "block";
    }

    _showHideActionGroupMap() {
        if (this._mapActionGroupsCheckbox.value) {
            this._actionGroupMapElem.classList.remove("hidden");
        } else {
            this._actionGroupMapElem.classList.add("hidden");
        }
    }

    _showHideAccountingFields() {
        if (this._enableAccountingCheckbox.value) {
            this._accountingElem.classList.remove("hidden");
        } else {
            this._accountingElem.classList.add("hidden");
        }
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _updateRoles() {
        if (this._radiusDefaultActionGroupId) {
            this._radiusDefaultActionGroupId.setOptions(this._buildActionGroupList());
        }
        return this._renderRoles();
    }

    _onTestRadiusClicked() {
        this.model.emitTestRadius(this._getRadiusDataFromFields());
    }

    _onRadiusEnableChange(showRadiusFields = true, refreshRadiusData = false) {
        this._renderRadiusFields();
        if (refreshRadiusData) {
            this.setRadiusDataFromModel();
        }
        if (showRadiusFields) {
            this._showRadiusFields();
            this._gatherRadiusDataAndUpdateModel();
        } else {
            this._hideRadiusFields();
            this.model.setSaveModel(undefined);
        }
    }

    _orgVisChanged(allOrHomeorSome) {
        this._setDefaultOrgVisValue(allOrHomeorSome);
        this._gatherRadiusDataAndUpdateModel();
    }

    _onSomeOrgVisChanged() {
        this._gatherRadiusDataAndUpdateModel();
    }

    _onMapActionGroupsChange() {
        this._showHideActionGroupMap();
        this._gatherRadiusDataAndUpdateModel();
    }

    _onEnableAccountingChanged(val) {
        this._showHideAccountingFields();
        if (!val) {
            // TODO - Fix dirty check before applying these
            // Lead undefined values to resolve other uservalue binding clashes
            // this._gatherRadiusDataAndUpdateModel(undefined,undefined,undefined,undefined,true);
            this._radiusControls.accountingHost.setValue("");
            this._radiusControls.accountingPort.setValue("");
            this._radiusControls.secondaryAccountingPort.setValue("");
            this._radiusControls.secondaryAccountingHost.setValue("");
        }
        this._gatherRadiusDataAndUpdateModel();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/
    //TODO - Nobody should be using methods on a Renderer directly so make all these methods private
    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}
module.exports = RadiusConfigRenderer;
